import { Dispatch, memo, SetStateAction, useMemo } from 'react';
import styled from '@emotion/styled';
import { useTranslate } from '@tolgee/react';
import Icon, { DownOutlined } from '@ant-design/icons';
import { Tooltip } from 'antd';
import { useSetConversationQuotedMsgDraft } from 'hooks/business/conversationDraft';
import { ContextMenuProps, isSupportMenu } from './ContextMenu';
import feedbackSvg from './assets/feedback.svg';
import quoteSvg from './assets/quote.svg';
import video2textSvg from './assets/video2text.svg';
import intentReportSvg from './assets/intentReport.svg';
import intentTestSvg from './assets/intentTest.svg';
import { ReactComponent as TranslateSvg } from '../../../assets/translate.svg';
import TranslatePopover from './TranslatePopover';
import { useConversationReadonlyValue } from 'hooks/business/conversation';
import { NewTodoIcon } from 'views/Conversation/Sider/ToDoBundle/NewTodo';
import { useOrgConfigStateValue } from 'hooks/business/useOrgConfig';
import { useChatContext } from '../../contexts/ChatContext';
import { useAuthorization } from 'components/Authorization';

export type Placement = 'right' | 'left';

const Container = styled.section<{ placement?: Placement }>`
  flex: 0 0 auto;
  display: flex;
  flex-direction: ${({ placement }) => (placement === 'right' ? 'row-reverse' : 'row')};
  height: 26px;
  margin: 0 6px;
  border-radius: 2px;
  > span {
    height: 24px;
    background: var(--fett-bg-panel);
    border: solid 1px var(--fett-border-strong);
    ${({ placement }) => `border-${placement === 'right' ? 'left' : 'right'}: none;`}
    cursor: pointer;
    &:hover {
      background: var(--fett-bg-app);
    }
    ${({ placement }) => `&:last-child {
      border-${placement === 'right' ? 'left' : 'right'}: solid 1px var(--fett-border-strong);
    }`}
    > img {
      padding: 0 4px;
    }
  }
`;

const Translate = styled.span`
  display: flex;
  align-items: center;
  gap: 0 2px;
  padding: 0 4px;
  color: var(--fett-text-tertiary);
  .anticon {
    font-size: 16px;
  }
  .anticon-down {
    font-size: 10px;
  }
`;

export interface ExtraMenuProps extends ContextMenuProps {
  channel: General.PlatformUpperCaseTunnel;
  placement?: Placement;
  videoText?: string;
  setVideoText?: Dispatch<SetStateAction<string | undefined>>;
}

function ExtraMenu(props: ExtraMenuProps) {
  const {
    uiMessage,
    conversation,
    targetLanguage,
    placement = 'left',
    videoText,
    setVideoText,
    onTranslate,
    onFeedback,
  } = props;

  const { raw } = uiMessage;

  const setQuotedMessage = useSetConversationQuotedMsgDraft();

  const readonly = useConversationReadonlyValue();

  const { t } = useTranslate();

  const { whiteListConfig: { ASR } = {} } = useOrgConfigStateValue();

  const { galaxyModule, isGalaxy } = useChatContext();

  const { payload } = useAuthorization<Oversea.Token.Payload>();

  const {
    isSupportExtra,
    isSupportFeedback,
    isSupportTranslate,
    isSupportReply,
    isSupportVideo2Text,
    isSupportHideVideoText,
    isSupportIntentReport,
  } = useMemo(
    () =>
      isSupportMenu(
        uiMessage,
        conversation,
        isGalaxy,
        readonly,
        videoText,
        undefined,
        galaxyModule,
      ),
    [uiMessage, conversation, isGalaxy, readonly, videoText, galaxyModule],
  );

  if (!isSupportExtra) {
    return null;
  }

  const feedback = isSupportFeedback && (
    <Tooltip title={t('scene.chatWorkspace.feedbackDialog.title')}>
      <span className="hoverVisible" onClick={() => onFeedback(raw)}>
        <img src={feedbackSvg} alt="feedback" />
      </span>
    </Tooltip>
  );

  const translate = isSupportTranslate && (
    <TranslatePopover
      getPopupContainer={(_) => _}
      placement="bottom"
      align={{ offset: [0, -6] }}
      message={raw}
      targetLanguage={targetLanguage}
      onTranslate={onTranslate}
    >
      <Translate title={t('common.action.translate')}>
        <Icon component={TranslateSvg} />
        <DownOutlined />
      </Translate>
    </TranslatePopover>
  );

  const quote = isSupportReply && (
    <Tooltip title={t('common.action.reply')}>
      <span
        className="hoverVisible"
        onClick={() => setQuotedMessage(raw.conversationId, raw.msgId)}
      >
        <img src={quoteSvg} alt="quote" />
      </span>
    </Tooltip>
  );

  const videoTextNode =
    ASR && setVideoText ? (
      isSupportVideo2Text ? (
        <Tooltip title={t('scene.chatWorkspace.messageMenu.action.videoToText')}>
          <span className="hoverVisible" onClick={() => setVideoText('')}>
            <img src={video2textSvg} alt="video2text" />
          </span>
        </Tooltip>
      ) : isSupportHideVideoText ? (
        <Tooltip title={t('scene.chatWorkspace.messageMenu.action.cancelVideoToText')}>
          <span className="hoverVisible" onClick={() => setVideoText(undefined)}>
            <img src={video2textSvg} alt="hideVideoText" />
          </span>
        </Tooltip>
      ) : null
    ) : null;

  const intentReport = isSupportIntentReport && (
    <Tooltip title="意图识别错误提报">
      <span
        className="hoverVisible"
        onClick={() =>
          window.parent.postMessage(
            {
              type: '意图识别错误提报',
              data: {
                msg: raw,
                orgId: payload?.orgId,
              },
            },
            '*',
          )
        }
      >
        <img src={intentReportSvg} alt="intentReport" />
      </span>
    </Tooltip>
  );

  const intentTest = isSupportIntentReport && (
    <Tooltip title="意图识别测试">
      <span
        className="hoverVisible"
        onClick={() =>
          window.parent.postMessage(
            {
              type: '意图识别测试',
              data: raw,
            },
            '*',
          )
        }
      >
        <img src={intentTestSvg} alt="intentTest" />
      </span>
    </Tooltip>
  );

  return (
    <Container placement={placement}>
      {intentTest}
      {intentReport}
      {videoTextNode}
      {raw.msgFromType === 'BUYER' && conversation.channel !== 'FACEBOOK' && (
        <NewTodoIcon messageId={raw.msgId} storeId={raw.storeId} />
      )}
      {feedback}
      {quote}
      {translate}
    </Container>
  );
}

export default memo(ExtraMenu);
